import React from "react";
import { FormControl, FormLabel } from '@mui/material';
import Grid from '@mui/material/Grid';
import DemographicSelect from './DemographicSelect';
import { useCase, useJurors } from '../../db/hooks';

export default ({caseId, options, demographics, setDemographics, setOptions, disabled}) => {

  const { caseData } = useCase(caseId);
  const { jurors } = useJurors(caseId);

  // Category configs are additive (demographicCategoryConfigs) — seed the
  // predefined options from them first, still unioning in any existing
  // freeform juror values so legacy off-list values remain visible. A case
  // with no configs at all (or a config-less category) falls back to
  // today's scan-existing-values-only behavior.
  const configsByName = React.useMemo(() => {
    const configs = caseData?.demographicCategoryConfigs || [];
    const map = {};
    for (const config of configs) {
      if (config && config.name) {
        map[config.name] = config;
      }
    }
    return map;
  }, [caseData?.demographicCategoryConfigs]);

  const tmpDemographicsOptions = React.useMemo(() => {
    const categories = caseData?.demographicCategories || [];
    const optionsMap = {};
    if (categories.length > 0) {
      for (const option of categories) {
        optionsMap[option] = new Set(configsByName[option]?.options || []);
      }
      for (const juror of jurors) {
        if (juror.demographics) {
          for (const oneDemographic of juror.demographics) {
            if (oneDemographic && categories.indexOf(oneDemographic.name) >= 0) {
              if (oneDemographic.value && oneDemographic.value.length > 0) {
                optionsMap[oneDemographic.name].add(oneDemographic.value);
              }
            }
          }
        }
      }
      // Convert Sets to label/value arrays
      for (const option of Object.keys(optionsMap)) {
        optionsMap[option] = [...optionsMap[option]].map((oneOption) => ({
          value: oneOption,
          label: oneOption,
        }));
      }
    }
    return optionsMap;
  }, [caseData?.demographicCategories, configsByName, jurors]);

  React.useEffect(() => {
    if (tmpDemographicsOptions && Object.keys(tmpDemographicsOptions).length > 0) {
      setOptions(tmpDemographicsOptions);
    }
  }, [Object.keys(tmpDemographicsOptions).length])

  return (
    <FormControl component={'fieldset'}
                 fullWidth={true}
                 style={{ border: '1px solid #CCC', borderRadius: '4px', padding: '15px 5px 5px 5px' }}>
      <FormLabel style={{
        padding: 4,
        backgroundColor: 'white',
        position: 'absolute',
        top: '-11px',
        left: '5px',
        fontSize: '12px',
      }}>Demographics</FormLabel>
      <Grid container spacing={1}>
        {Object.keys(options).map(demographicsOption => {

          function handleSelected(newValue) {

            let newDemos = [...demographics].filter(d => !!d && d.value && d.value.length > 0);
            let indexOfExisting = newDemos.findIndex((oneDemo) => oneDemo.name === demographicsOption);
            if (indexOfExisting >= 0) {
              if (newValue !== null) {
                newDemos[indexOfExisting] = { name: demographicsOption, value: newValue };
              } else {
                newDemos.splice(indexOfExisting, 1);
              }
            } else {
              newDemos.push({ name: demographicsOption, value: newValue });
            }
            setDemographics(newDemos);
          }

          let foundValue = demographics.filter(demo => !!demo).find(demo => demo.name === demographicsOption),
            value = null,
            tmpOptions = options[demographicsOption];
          if (foundValue && foundValue.value) {
            value = { value: foundValue.value, label: foundValue.value };

            let foundOption = options[demographicsOption].find(o => o.value === foundValue.value);
            if (!foundOption) {

              tmpOptions.push({
                value: foundValue.value,
                label: foundValue.value,
              });
            }
          }

          const allowFreeform = configsByName[demographicsOption]?.allowFreeform !== false;

          return (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={`demographic-option-${demographicsOption}`}>
              <DemographicSelect name={demographicsOption}
                                 value={value}
                                 disabled={disabled}
                                 options={tmpOptions}
                                 allowFreeform={allowFreeform}
                                 onCreateOption={(inputValue) => {

                                   setOptions({
                                     ...options,
                                     [demographicsOption]: [...options[demographicsOption], {
                                       value: inputValue,
                                       label: inputValue,
                                     }],
                                   });

                                   handleSelected(inputValue);
                                 }}
                                 onChange={(newValue, actionMeta) => {
                                   handleSelected(newValue ? newValue.value : null);
                                 }}
              />
            </Grid>
          );
        })}
      </Grid>
    </FormControl>
  )
}
